import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import Fade from '@mui/material/Fade';

const faqs = [
  {
    question: "I only have an idea. Is that enough?",
    answer: "Yes. Most projects start with nothing but an idea. On a free 15-minute call we talk through what you want, and I tell you what it would take and whether it's worth building."
  },
  {
    question: "What do you build?",
    answer: "Web apps, iPhone and Android apps, AI-powered features and automation, plus the secure hosting behind them, in the cloud or on your own hardware."
  },
  {
    question: "How do you price projects?",
    answer: "Fixed scope, fixed price. After the free call, if it's a fit, you get a plain-English plan and a fixed quote. No hourly billing, no surprise costs."
  },
  {
    question: "Who will I work with?",
    answer: "Me, Dale Arbogast. I'm the engineer who does the work, with no account managers or offshore handoffs."
  },
  {
    question: "I built an app with AI and it's falling apart. Can you help?",
    answer: "Yes. I clean up DIY and AI-built apps by cutting the bloat, closing security gaps and adding tests and backups, so it's something you can grow on."
  },
  {
    question: "Do I own the code?",
    answer: "Always. You own the code, the accounts and everything else, and I walk you through it at hand-off."
  }
];

const FAQChatbox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setSelectedFAQ(null);
      setShowAnswer(false);
    }
  };

  const handleFAQClick = (index) => {
    setSelectedFAQ(index);
    setTimeout(() => setShowAnswer(true), 100);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1300,
      }}
    >
      <Collapse in={isOpen} timeout={300}>
        <Paper
          elevation={6}
          sx={{
            width: { xs: 320, sm: 360 },
            maxHeight: 420,
            mb: 2,
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: '#111318',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Quick Answers
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{
                  color: 'white',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <MinimizeIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{
                  color: 'white',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: '#f8fafc',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#d1d5db',
                borderRadius: '2px',
              },
            }}
          >
            {/* Bot Welcome Message */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  bgcolor: '#111318',
                  color: 'white',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                AI
              </Box>
              <Box
                sx={{
                  bgcolor: 'white',
                  p: 1.5,
                  borderRadius: '8px 8px 8px 0',
                  maxWidth: '85%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', color: '#374151', mb: 1 }}>
                  Have questions about working with us? Here are some common ones:
                </Typography>
              </Box>
            </Box>

            {/* FAQ Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 4 }}>
              {faqs.map((faq, index) => (
                <Chip
                  key={index}
                  label={faq.question}
                  onClick={() => handleFAQClick(index)}
                  size="small"
                  sx={{
                    bgcolor: selectedFAQ === index ? '#111318' : 'white',
                    color: selectedFAQ === index ? 'white' : '#1d1d1f',
                    fontSize: '0.75rem',
                    height: 'auto',
                    py: 0.5,
                    '&:hover': {
                      bgcolor: selectedFAQ === index ? '#111318' : '#f0f0f0',
                    },
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                />
              ))}
            </Box>

            {/* Answer Display */}
            {selectedFAQ !== null && (
              <Fade in={showAnswer}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                  <Box
                    sx={{
                      bgcolor: '#111318',
                      color: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    AI
                  </Box>
                  <Box
                    sx={{
                      bgcolor: 'white',
                      p: 1.5,
                      borderRadius: '8px 8px 8px 0',
                      maxWidth: '85%',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.85rem', color: '#374151', lineHeight: 1.5 }}>
                      {faqs[selectedFAQ].answer}
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: '1px solid #e2e8f0',
              bgcolor: 'white',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#86868b',
                textAlign: 'center',
              }}
            >
              Ready to talk?{' '}
              <Link
                to="/contact"
                style={{
                  color: '#111318',
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                Book a free 15-minute call
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Collapse>

      {/* Floating Chat Button */}
      {!isOpen && (
        <Fade in={true}>
          <IconButton
            onClick={handleToggle}
            aria-label="Open frequently asked questions"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 56,
              height: 56,
              background: '#F5C518',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
              '&:hover': {
                background: '#F5C518',
                filter: 'brightness(1.08)',
                transform: 'scale(1.05)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.45)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <ChatIcon sx={{ fontSize: 26, color: '#111318' }} />
          </IconButton>
        </Fade>
      )}
    </Box>
  );
};

export default FAQChatbox;
